/**
 * Utilidades para detectar dispositivos iOS con Dynamic Island
 * y aplicar estilos específicos para el tab bar
 */
import React from 'react';

export interface DeviceInfo {
  hasNotch: boolean;
  hasDynamicIsland: boolean;
  isIOS: boolean;
  isAndroid: boolean;
  hasBottomBar: boolean;
  deviceModel: string;
  safeAreaBottom: number;
  viewportHeight: number;
  screenHeight: number;
}

/**
 * Detecta si el dispositivo es un iPhone con Dynamic Island
 */
export function detectDynamicIsland(): boolean {
  if (typeof window === 'undefined') return false;

  const width = window.screen.width;
  const height = window.screen.height;
  const pixelRatio = window.devicePixelRatio || 1;
  
  // Dimensiones de dispositivos con Dynamic Island
  const dynamicIslandDevices = [
    // iPhone 14 Pro
    { width: 393, height: 852, ratio: 3 },
    // iPhone 14 Pro Max
    { width: 430, height: 932, ratio: 3 },
    // iPhone 15 Pro
    { width: 393, height: 852, ratio: 3 },
    // iPhone 15 Pro Max
    { width: 430, height: 932, ratio: 3 },
    // iPhone 16 Pro (estimado)
    { width: 402, height: 874, ratio: 3 },
    // iPhone 16 Pro Max (estimado)
    { width: 440, height: 956, ratio: 3 }
  ];
  
  return dynamicIslandDevices.some(device => 
    (width === device.width && height === device.height && pixelRatio === device.ratio) ||
    (width === device.height && height === device.width && pixelRatio === device.ratio) // landscape
  );
}

/**
 * Detecta si el dispositivo es iOS
 */
export function isIOSDevice(): boolean {
  if (typeof window === 'undefined') return false;

  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
         (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

/**
 * Detecta si el dispositivo es Android
 */
export function isAndroidDevice(): boolean {
  if (typeof window === 'undefined') return false;

  return /Android/.test(navigator.userAgent);
}

/**
 * Detecta si el navegador tiene barra inferior dinámica
 * (común en Chrome móvil, Samsung Internet, etc.)
 */
export function hasBottomNavigationBar(): boolean {
  if (typeof window === 'undefined') return false;

  const screenHeight = window.screen.height;
  const viewportHeight = window.innerHeight;
  const heightDifference = screenHeight - viewportHeight;

  // Detectar navegadores específicos que suelen tener barras inferiores
  const userAgent = navigator.userAgent.toLowerCase();
  const hasChromeMobile = userAgent.includes('chrome') && userAgent.includes('mobile');
  const hasSamsungBrowser = userAgent.includes('samsungbrowser');
  const hasEdgeMobile = userAgent.includes('edg') && userAgent.includes('mobile');

  // Si la diferencia es significativa (más de 80px) o es un navegador conocido por tener barras
  return heightDifference > 80 || hasChromeMobile || hasSamsungBrowser || hasEdgeMobile;
}

/**
 * Obtiene información completa del dispositivo
 */
export function getDeviceInfo(): DeviceInfo {
  const isIOS = isIOSDevice();
  const isAndroid = isAndroidDevice();
  const hasDynamicIsland = detectDynamicIsland();
  const hasNotch = isIOS && !hasDynamicIsland; // Simplificado
  const hasBottomBar = hasBottomNavigationBar();

  // Intentar obtener el safe area bottom
  let safeAreaBottom = 0;
  if (typeof window !== 'undefined' && CSS.supports('padding', 'env(safe-area-inset-bottom)')) {
    const testElement = document.createElement('div');
    testElement.style.paddingBottom = 'env(safe-area-inset-bottom)';
    document.body.appendChild(testElement);
    const computedStyle = window.getComputedStyle(testElement);
    safeAreaBottom = parseInt(computedStyle.paddingBottom) || 0;
    document.body.removeChild(testElement);
  }

  return {
    hasNotch,
    hasDynamicIsland,
    isIOS,
    isAndroid,
    hasBottomBar,
    deviceModel: getDeviceModel(),
    safeAreaBottom,
    viewportHeight: window.innerHeight,
    screenHeight: window.screen.height
  };
}

/**
 * Obtiene el modelo del dispositivo (simplificado)
 */
function getDeviceModel(): string {
  if (!isIOSDevice()) return 'unknown';

  const width = window.screen.width;
  const height = window.screen.height;
  const pixelRatio = window.devicePixelRatio || 1;
  
  // iPhone con Dynamic Island
  if (width === 393 && height === 852 && pixelRatio === 3) return 'iPhone 14/15 Pro';
  if (width === 430 && height === 932 && pixelRatio === 3) return 'iPhone 14/15 Pro Max';
  if (width === 402 && height === 874 && pixelRatio === 3) return 'iPhone 16 Pro';
  if (width === 440 && height === 956 && pixelRatio === 3) return 'iPhone 16 Pro Max';
  
  return 'iPhone';
}

/**
 * Aplica clases CSS específicas para el dispositivo detectado
 */
export function applyDeviceSpecificStyles(): void {
  if (typeof document === 'undefined') return;

  const deviceInfo = getDeviceInfo();
  const body = document.body;

  // Limpiar clases previas
  body.classList.remove('has-notch', 'has-dynamic-island', 'ios-device', 'android-device', 'iphone-with-safe-area', 'has-bottom-bar');

  if (deviceInfo.isIOS) {
    body.classList.add('ios-device');

    // Detectar si es un iPhone moderno con safe area significativa
    const screenWidth = window.screen.width;
    const screenHeight = window.screen.height;
    const pixelRatio = window.devicePixelRatio || 1;

    // Si es un iPhone con pantalla grande y alta resolución, probablemente tiene Dynamic Island o notch
    if (pixelRatio === 3 && (screenWidth >= 375 || screenHeight >= 812)) {
      body.classList.add('iphone-with-safe-area');
      console.log('📱 iPhone moderno detectado - aplicando margen para safe area');
    }
  }

  if (deviceInfo.isAndroid) {
    body.classList.add('android-device');
    console.log('🤖 Dispositivo Android detectado');
  }

  if (deviceInfo.hasBottomBar) {
    body.classList.add('has-bottom-bar');
    const heightDiff = deviceInfo.screenHeight - deviceInfo.viewportHeight;
    console.log('📱 Barra inferior detectada - diferencia de altura:', heightDiff + 'px');
  }

  if (deviceInfo.hasDynamicIsland) {
    body.classList.add('has-dynamic-island');
    console.log('🏝️ Dynamic Island detectado:', deviceInfo.deviceModel);
  } else if (deviceInfo.hasNotch) {
    body.classList.add('has-notch');
    console.log('📱 Notch detectado:', deviceInfo.deviceModel);
  }

  // Aplicar variable CSS personalizada para el safe area
  if (deviceInfo.safeAreaBottom > 0) {
    document.documentElement.style.setProperty('--detected-safe-area-bottom', `${deviceInfo.safeAreaBottom}px`);
  }

  // Log para debugging
  console.log('Device Info:', {
    userAgent: navigator.userAgent,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    viewportHeight: window.innerHeight,
    heightDifference: window.screen.height - window.innerHeight,
    pixelRatio: window.devicePixelRatio || 1,
    isIOS: deviceInfo.isIOS,
    isAndroid: deviceInfo.isAndroid,
    hasBottomBar: deviceInfo.hasBottomBar,
    hasDynamicIsland: deviceInfo.hasDynamicIsland,
    safeAreaBottom: deviceInfo.safeAreaBottom
  });
}

/**
 * Hook para usar en componentes React
 */
export function useDeviceDetection() {
  const [deviceInfo, setDeviceInfo] = React.useState<DeviceInfo>({
    hasNotch: false,
    hasDynamicIsland: false,
    isIOS: false,
    isAndroid: false,
    hasBottomBar: false,
    deviceModel: 'unknown',
    safeAreaBottom: 0,
    viewportHeight: 0,
    screenHeight: 0
  });
  
  React.useEffect(() => {
    const updateDeviceInfo = () => {
      const info = getDeviceInfo();
      setDeviceInfo(info);
      applyDeviceSpecificStyles();
    };

    // Detectar inicialmente
    updateDeviceInfo();

    // Escuchar cambios en el tamaño del viewport (barras que aparecen/desaparecen)
    const handleResize = () => {
      setTimeout(updateDeviceInfo, 100); // Pequeño delay para que se estabilice
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);
  
  return deviceInfo;
}
